import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../../../contexts/Language";
import { appLanguages, rememberLanguageChoice } from "../../../utils/languageUtils";
import { Flag } from "../../graphic/Flag";
import { Icon } from "../../graphic/Icon";
import { switchSitePathLanguage } from "../../../utils/siteRoutes";
import styles from "./SiteLanguageMenu.scss";

const msgKey = "siteLanguageMenu";

/**
 * Flag button that switches the UI language on the public pages.
 * Pages with the language in the URL (/no/pricing) go to the same page in the other language.
 */
export const SiteLanguageMenu = ({ className }) => {
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const { language, setLanguage, pageLanguage } = useContext(LanguageContext);
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onMouseDown = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => event.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    const selectLanguage = (appLanguage) => {
        rememberLanguageChoice(appLanguage);
        setLanguage(appLanguage);
        if (pageLanguage) {
            navigate(
                `${switchSitePathLanguage(location.pathname, appLanguage.locale)}${location.search}${location.hash}`
            );
        }
        setOpen(false);
    };

    return (
        <div ref={menuRef} className={classNames(styles.base, className)}>
            <button
                type="button"
                className={styles.toggle}
                aria-label={intl.formatMessage({ id: `${msgKey}.language` }, { language: language.name })}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                <Flag code={language.flag} className={styles.flag} />
            </button>
            {open ? (
                <div className={styles.menu} role="menu">
                    {appLanguages.map((appLanguage) => (
                        <button
                            key={appLanguage.locale}
                            type="button"
                            role="menuitemradio"
                            aria-checked={appLanguage.locale === language.locale}
                            className={styles.item}
                            onClick={() => selectLanguage(appLanguage)}
                        >
                            <Flag code={appLanguage.flag} className={styles.flag} />
                            <span className={styles.label}>{appLanguage.name}</span>
                            {appLanguage.locale === language.locale ? <Icon name="check" /> : null}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};
